import React, { useMemo, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useGlobalFilters } from '../../hooks/useGlobalFilters';
import { dynamicChartStore } from './DynamicChartStore';
import { predictionTrendsStore } from "../../Analysis_A/PredictionTrendsStore";
import DataWindowSlider from '../../components/DataWindowSliderPV';
import ChartPointDataModal from '../../components/ChartPointDataModal';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ScatterController,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './ActivityAnalysis.scss';
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

// Регистрация компонентов Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ScatterController
);

// Определение уровней тревоги и их цветов (синхронизировано с данными API)
const alarmLevels = {
  none: { color: '#E0E0E0', label: 'Очень низкая' }, // Серый
  low: { color: '#A0FFA0', label: 'Низкая' }, // Светло-зеленый
  medium: { color: '#FFFFA0', label: 'Средняя' }, // Желтый
  medium_alt: { color: '#FFE68C', label: 'Высокая' }, // Оранжевый
  high: { color: '#FFB9B9', label: 'Очень Высокая' }, // Светло-красный  
};

function formatNumber(val) {
  if (typeof val === "number") {
    return val.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  if (!isNaN(Number(val))) {
    return Number(val).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  return val ?? "";
}

function formatDate(date) {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ru-RU");
}

function getGroupNamesByIds(ids) {
  if (!Array.isArray(ids)) return [];
  return ids.map(id => {
    const group = predictionTrendsStore.groups.find(g => String(g.id) === String(id));
    return group ? group.name : id;
  });
}

const DynamicChart = observer(() => {
  const globalFilters = useGlobalFilters();
  const store = dynamicChartStore;
  const [dataWindow, setDataWindow] = React.useState({ start: 0, end: 100 });
  const [aggregation, setAggregation] = React.useState('day'); // 'day' | 'week'
  const chartInstanceRef = useRef(null);
  const [modalState, setModalState] = React.useState({
    visible: false,
    rows: [],
    dateLabel: "",
  });
  const [modalLoading, setModalLoading] = React.useState(false);
  const [modalError, setModalError] = React.useState(null);

  // Функция для обновления окна данных
  const updateWindow = (value) => {
    const newStart = Number(value);
    setDataWindow({ start: newStart, end: newStart + 100 });
  };

  // Функция агрегации данных
  const aggregateData = (data, period) => {
    if (!data || data.length === 0) return [];

    const grouped = {};
    
    data.forEach(item => {
      const date = new Date(item.time);
      let key;
      
      switch (period) {
        case 'week':
          // Начало недели (понедельник)
          const dayOfWeek = date.getDay();
          const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // adjust when day is sunday
          const monday = new Date(date.setDate(diff));
          key = monday.toISOString().split('T')[0];
          break;
        default: // 'day'
          key = date.toISOString().split('T')[0];
          break;
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          time: key,
          activities: [],
          avg_activities: [],
          alarm_levels: []
        };
      }
      
      // Собираем все значения для усреднения
      grouped[key].activities.push(item.activity || 0);
      grouped[key].avg_activities.push(item.avg_activity || 0);
      grouped[key].alarm_levels.push(item.alarm_level);
    });

    // Вычисляем средние значения и определяем преобладающий уровень тревоги
    return Object.values(grouped)
      .map(group => {
        // Среднее значение активности
        const avgActivity = group.activities.reduce((sum, val) => sum + val, 0) / group.activities.length;
        const avgOfAvg = group.avg_activities.reduce((sum, val) => sum + val, 0) / group.avg_activities.length;
        
        // Определяем преобладающий уровень тревоги
        const alarmLevelCounts = {};
        group.alarm_levels.forEach(level => {
          alarmLevelCounts[level] = (alarmLevelCounts[level] || 0) + 1;
        });
        const predominantAlarmLevel = Object.keys(alarmLevelCounts).reduce((a, b) => 
          alarmLevelCounts[a] > alarmLevelCounts[b] ? a : b
        );
        
        return {
          time: group.time,
          activity: avgActivity,
          avg_activity: avgOfAvg,
          alarm_level: predominantAlarmLevel
        };
      })
      .sort((a, b) => new Date(a.time) - new Date(b.time));
  };

  // Автоматически обновляем фильтры и подгружаем данные при изменении глобальных фильтров
  React.useEffect(() => {
    store.setFilters({
      startDate: globalFilters.startDate,
      endDate: globalFilters.endDate,
      selectedGroupIds: globalFilters.selectedGroups,
    });
    store.fetchData();
    // eslint-disable-next-line
  }, [globalFilters.filtersString]);

  // Применяем агрегацию и окно данных для видимых данных
  const allData = useMemo(() => {
    const rawData = store.data || [];
    return aggregateData(rawData, aggregation);
  }, [store.data, aggregation]);

  const visibleData = useMemo(() => {
    if (allData.length === 0) return [];
    const startIndex = Math.min(dataWindow.start, allData.length - 1);
    const endIndex = Math.min(dataWindow.end, allData.length);
    return allData.slice(startIndex, endIndex);
  }, [allData, dataWindow]);

  // Максимальная длина данных для слайдера
  const maxDataLength = allData.length;

  // Текст диапазона дат
  const dateRangeText = useMemo(() => {
    if (allData.length === 0) {
      return 'Нет данных';
    }
    
    const startIndex = Math.min(dataWindow.start, allData.length - 1);
    const endIndex = Math.min(dataWindow.end - 1, allData.length - 1);
    
    const startDate = new Date(allData[startIndex]?.time);
    const endDate = new Date(allData[endIndex]?.time);
    
    const formatDate = (date) => {
      return !isNaN(date) ? date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }) : 'Недоступно';
    };
    
    return `Показаны данные: ${formatDate(startDate)} - ${formatDate(endDate)}`;
  }, [allData, dataWindow]);

  // Сброс окна данных при получении новых данных или изменении агрегации
  React.useEffect(() => {
    setDataWindow({ start: 0, end: 100 });
  }, [allData.length, aggregation]);

  const getModalDateRange = useCallback((label) => {
    if (!label) return null;
    const baseDate = new Date(label);
    if (Number.isNaN(baseDate.getTime())) return null;

    const startISO = baseDate.toISOString().split("T")[0];
    let endDate = new Date(baseDate);

    if (aggregation === "week") {
      endDate = new Date(baseDate);
      endDate.setDate(baseDate.getDate() + 6);
    }

    const endISO = endDate.toISOString().split("T")[0];
    const prettyLabel =
      aggregation === "week"
        ? `${baseDate.toLocaleDateString("ru-RU")} - ${endDate.toLocaleDateString("ru-RU")}`
        : baseDate.toLocaleDateString("ru-RU");

    return { start: startISO, end: endISO, label: prettyLabel };
  }, [aggregation]);

  const handleCloseModal = useCallback(() => {
    setModalState({ visible: false, rows: [], dateLabel: "" });
    setModalError(null);
  }, []);

  const handleChartClick = useCallback(
    async (event) => {
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance) return;

      const nativeEvent = event?.native || event?.nativeEvent || event;
      if (!nativeEvent) return;

      const elements = chartInstance.getElementsAtEventForMode(
        nativeEvent,
        "nearest",
        { intersect: true },
        false
      );

      if (!elements?.length) return;

      const element = elements.find((el) => {
        const dataset = chartInstance.data?.datasets?.[el.datasetIndex];
        return dataset?.type === "bar";
      });

      if (!element) return;

      const label = chartInstance.data?.labels?.[element.index];
      const range = getModalDateRange(label);
      if (!range) return;
      if (!globalFilters.selectedGroups.length) return;

      setModalLoading(true);
      try {
        const rows = await store.fetchPostsForModal({
          groupIds: globalFilters.selectedGroups,
          startDate: range.start,
          endDate: range.end,
        });

        setModalState({
          visible: true,
          rows,
          dateLabel: range.label,
        });
        setModalError(null);
      } catch (error) {
        console.error("Не удалось загрузить посты для модального окна:", error);
        setModalState({
          visible: true,
          rows: [],
          dateLabel: range.label,
        });
        setModalError("Не удалось загрузить посты для выбранного периода.");
      } finally {
        setModalLoading(false);
      }
    },
    [getModalDateRange, globalFilters.selectedGroups, store]
  );

  // Данные для графика
  const chartData = useMemo(
    () => ({
      labels: visibleData.map((item) => item.time), // Используем ISO-строку напрямую
      datasets: [
        {
          type: 'line',
          label: 'Средняя активность',
          data: visibleData.map((item) => item.avg_activity),
          borderColor: '#4a90e2',
          backgroundColor: '#4a90e2', // Для синего квадратика в легенде
          fill: false,
          tension: 0.4,
          pointRadius: 0, // Убираем точки на линии
        },
        {
          type: 'bar',
          label: 'Активность',
          data: visibleData.map((item) => item.activity),
          backgroundColor: visibleData.map((item) => {
            // Проверяем alarm_level и возвращаем соответствующий цвет
            switch (item.alarm_level) {
              case '#A0FFA0':
                return alarmLevels.low.color;
              case '#FFE68C':
                return alarmLevels.medium.color;
              case '#FFFFA0':
                return alarmLevels.medium_alt.color;
              case '#FFB9B9':
                return alarmLevels.high.color;
              case '#E0E0E0':
                return alarmLevels.none.color;
              default:
                console.warn(`Неизвестный alarm_level: ${item.alarm_level}`);
                return alarmLevels.none.color;
            }
          }),
          borderColor: 'transparent',
          borderWidth: 0,
          barPercentage: 0.5,
          categoryPercentage: 0.5,
          hidden: false, // Убедимся, что бары видны
        },
        {
          type: 'scatter',
          label: 'Уровень тревоги',
          data: visibleData.map((item) => ({
            x: item.time,
            y: item.activity,
          })),
          backgroundColor: visibleData.map((item) => {
            switch (item.alarm_level) {
              case '#A0FFA0':
                return alarmLevels.low.color;
              case '#FFE68C':
                return alarmLevels.medium.color;
              case '#FFFFA0':
                return alarmLevels.medium_alt.color;
              case '#FFB9B9':
                return alarmLevels.high.color;
              case '#E0E0E0':
                return alarmLevels.none.color;
              default:
                return alarmLevels.none.color;
            }
          }),
          pointRadius: 3,
          pointHoverRadius: 5,
          hidden: true, // Скрываем scatter
        },
      ],
    }),
    [visibleData]
  );

  // Настройки графика
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 2.5,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif" },
          usePointStyle: true,
          pointStyle: 'rect',
          filter: (legendItem) => legendItem.text === 'Средняя активность', // Только для средней активности
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#2d3748',
        titleColor: '#fff',
        bodyColor: '#fff',
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.y || 0;
            const index = context.dataIndex;
            const alarmLevel = visibleData[index]?.alarm_level;
            let alarmLabel = alarmLevels.none.label;
            if (alarmLevel === '#A0FFA0') alarmLabel = alarmLevels.low.label;
            if (alarmLevel === '#FFE68C' || alarmLevel === '#FFFFA0') alarmLabel = alarmLevels.medium.label;
            if (alarmLevel === '#FFB9B9') alarmLabel = alarmLevels.high.label;
            return `${datasetLabel}: ${value} (${alarmLabel})`;
          },
          title: (tooltipItems) => {
            const label = tooltipItems[0].label;
            const date = new Date(label);
            return isNaN(date.getTime()) ? 'Недоступно' : date.toLocaleDateString('ru-RU');
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Время',
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif", weight: '600' },
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 20,
          maxRotation: 45,
          minRotation: 45,
          color: '#4a5568',
          font: { size: 12, family: "'Inter', sans-serif" },
          callback: function (value) {
            const label = this.getLabelForValue(value);
            if (!label || typeof label !== 'string') {
              console.warn(`Некорректная метка времени: ${label}`);
              return 'Недоступно';
            }
            const date = new Date(label);
            if (isNaN(date.getTime())) {
              console.warn(`Не удалось распарсить дату: ${label}`);
              return 'Недоступно';
            }
            return date.toLocaleDateString('ru-RU'); // Формат дд.мм.гггг
          },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Активность',
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif", weight: '600' },
        },
        beginAtZero: true,
        ticks: {
          color: '#4a5568',
          font: { size: 12, family: "'Inter', sans-serif" },
        },
      },
    },
    devicePixelRatio: 2,
  };

  // Экспорт в Excel
  const handleExportExcel = async () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = "Активность_и_средняя_активность";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.xlsx`;
    const allDataForExport = store.data || [];
    const ws_data = [
      ["Date", "activity", "avg_activity"],
      ...allDataForExport.map(d => [
        formatDate(d.time),
        formatNumber(d.activity),
        formatNumber(d.avg_activity)
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Данные");
    XLSX.writeFile(wb, fileName);
  };

  // Экспорт в PDF
  const handleExportPDF = async () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = "Активность_и_средняя_активность";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.pdf`;
    const allDataForExportPDF = store.data || [];
    const input = document.querySelector("#activity-chart-container");
    if (!input) { alert("График не найден для экспорта!"); return; }
    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.addImage(imgData, "PNG", 10, 10, 270, 80);
    autoTable(pdf, {
      startY: 100,
      head: [["Date", "activity", "avg_activity"]],
      body: allDataForExportPDF.map(d => [
        formatDate(d.time),
        formatNumber(d.activity),
        formatNumber(d.avg_activity)
      ]),
      styles: { font: "helvetica", fontSize: 10 }
    });
    pdf.save(fileName);
  };

  // Рендеринг компонента
  return (
    <div className="activity-analysis">
      <div className="activity-analysis-container">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            {/* Селектор агрегации */}
            <select 
              value={aggregation} 
              onChange={e => setAggregation(e.target.value)} 
              style={{ 
                fontSize: 16, 
                padding: '6px 12px', 
                borderRadius: 8, 
                border: '1px solid #1976d2', 
                color: '#1976d2', 
                fontWeight: 500,
                backgroundColor: '#fff'
              }}
            >
              <option value="day">По дням</option>
              <option value="week">По неделям</option>
            </select>
          </div>
          {/* Кнопки справа под графиком */}
          <div className="activity-analysis-content-box" style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
            padding: 24,
            margin: '0 auto',
            maxWidth: '100%',
            width: '100%',
            minWidth: '1200px',
            
          }}>
            {/* Кастомная легенда для уровней тревоги */}
            <div className="activity-analysis-custom-legend" style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {Object.entries(alarmLevels).map(([key, { color, label }]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '16px',
                      height: '16px',
                      backgroundColor: color,
                      marginRight: '8px',
                      borderRadius: '4px',
                    }}
                  />
                  <span style={{ color: '#2d3748', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
            {/* Слайдер прокрутки данных */}
            {maxDataLength > 100 && !store.loading && !store.error && visibleData.length > 0 && (
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                <DataWindowSlider
                  aggregation={aggregation}
                  dataWindow={dataWindow}
                  maxDataLength={maxDataLength}
                  updateWindow={updateWindow}
                  dateRangeText={dateRangeText}
                />
              </div>
            )}

            {/* График */}
            <div className="activity-analysis-chart-wrapper" id="activity-chart-container" style={{
              height: 'auto',
              width: '100%',
              background: '#fff',
              borderRadius: 8,
              padding: '16px',
              position: 'relative'
            }}>
              {store.loading ? (
                <div className="activity-analysis-no-data">Загрузка...</div>
              ) : store.error ? (
                <div className="activity-analysis-no-data">Ошибка: {store.error.message}</div>
              ) : visibleData.length > 0 ? (
                <div style={{ height: '300px', width: '100%' }}>
                  <Bar
                    ref={(chart) => {
                      chartInstanceRef.current = chart;
                    }}
                    data={chartData}
                    options={chartOptions}
                    onClick={handleChartClick}
                  />
                </div>
              ) : globalFilters.selectedGroups.length ? (
                <div className="activity-analysis-no-data">К сожалению на данный момент для выбранных групп  нет данных, попробуйте выбрать другие сообщества</div>
              ) : (
                <div className="activity-analysis-no-data">Выберите сообщества в верхней панели для отображения графика</div>
              )}
            </div>
            {visibleData.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, marginRight: 24 }}>
                <button className="activity-analysis-export-btn" onClick={handleExportExcel}>Экспорт в Excel</button>
                <button className="activity-analysis-export-btn" onClick={handleExportPDF}>Экспорт в PDF</button>
              </div>
            )}
            {modalError && (
              <div style={{ color: '#d93025', marginTop: 12, textAlign: 'right' }}>
                {modalError}
              </div>
            )}
          </div>
        </div>
      </div>
      {modalState.visible && (
        <ChartPointDataModal
          date={modalState.dateLabel}
          rows={modalState.rows}
          onClose={handleCloseModal}
        />
      )}
      {modalLoading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 18,
            fontWeight: 500,
          }}
        >
          Загрузка постов...
        </div>
      )}
    </div>
  );
});

export default DynamicChart;